import { absoluteValue } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { backpackAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { type InterestPayment, interestPaymentsResponseSchema } from './schema'

/** 1 ページあたり API が返す最大件数 (Backpack 仕様の上限) */
const PAGE_LIMIT = 1000

/**
 * 安全装置として 1 回の `fetchApiData` 呼び出しで取得する最大ページ数。
 * 1000 件 × 1000 ページ = 100 万件まで耐える設計とする。
 */
const MAX_PAGES = 1000

/**
 * 借入元本に組み入れられる利息の paymentType。
 * 返済時に `borrow-lend-history` の返済額として計上されるため Action を生成しない。
 */
const borrowInterestPaymentTypes = new Set<InterestPayment['paymentType']>([
  'Borrow',
  'EntryFee',
])

/**
 * naive ISO date-time を UTC として解釈し UNIX milliseconds に変換する。
 */
const parseTimestamp = (value: string): number => {
  const hasTimezone = /([Zz]|[+-]\d{2}:?\d{2})$/.test(value)
  const isoString = hasTimezone ? value : `${value}Z`
  const ms = Date.parse(isoString)
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid Backpack interest timestamp: ${value}`)
  }
  return ms
}

/**
 * Backpack の borrow lend 利息支払い履歴を取得し、CSV (`backpack-interest-payment`) と同じ方針で
 * `interest` (受取) / `interest-fee` (支払) Action を生成する。
 * 方向は `paymentType` ではなく `quantity` の符号で判定する:
 * - 正 (例: `Lend`): 受取 (`interest`, in)
 * - 負 (例: `UnrealizedNegativePnl`): 支払 (`interest-fee`, out)
 *
 * `Borrow` / `EntryFee` (借入利息) は借入元本に組み入れられ、`borrow-lend-history` の返済額に含まれる。
 * 会計処理で返済額のうち借入額を超えた分が自動で `interest-fee` になるため、ここでは計上しない。
 *
 * 当 endpoint は `from`/`to` フィルタを持たず offset ベースの pagination のみ。
 * sortDirection=Desc (新しい順) で取得し、 framework から渡された `from` より古い record に達したら
 * early-stop することで効率化する。
 */
export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  createHeaders,
  from,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const collected: (InterestPayment & { timestampMs: number })[] = []
  let earlyStop = false

  for (let page = 0; page < MAX_PAGES; page++) {
    const response = await client.run({
      method: 'GET',
      path: '/wapi/v1/history/interest',
      query: {
        limit: PAGE_LIMIT.toString(),
        offset: (page * PAGE_LIMIT).toString(),
        sortDirection: 'Desc',
      },
      responseSchema: interestPaymentsResponseSchema,
    })

    for (const item of response) {
      const timestampMs = parseTimestamp(item.timestamp)
      // sortDirection=Desc のため timestamp は逐次古くなる。
      // pending 開始時刻 `from` より古くなった時点で以降は対象外なので終了。
      if (from !== undefined && timestampMs < from) {
        earlyStop = true
        break
      }
      // 未来側のフィルタ (to 指定がある場合) は client-side で除外
      if (to !== undefined && timestampMs > to) {
        continue
      }
      collected.push({ ...item, timestampMs })
    }

    if (earlyStop || response.length < PAGE_LIMIT) {
      break
    }
  }

  collected.sort((a, b) => a.timestampMs - b.timestampMs)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of collected) {
    fetchedFrom =
      fetchedFrom === undefined
        ? item.timestampMs
        : Math.min(fetchedFrom, item.timestampMs)
    fetchedTo =
      fetchedTo === undefined
        ? item.timestampMs
        : Math.max(fetchedTo, item.timestampMs)

    if (item.quantity.value === 0n) {
      continue
    }
    if (borrowInterestPaymentTypes.has(item.paymentType)) {
      continue
    }

    const asset = getAsset(item.symbol)
    if (!asset) {
      throw new AssetNotFoundException(item.symbol)
    }

    const isReceived = item.quantity.value > 0n

    actions.push({
      comment: `market symbol: ${item.marketSymbol} / interest rate: ${item.interestRate.value.toString()} / payment type: ${item.paymentType}`,
      evidence: 'system-rule',
      order: 0,
      source: `${backpackAccountCategory}_${config.type}_${item.positionId}_${item.paymentType}_${item.timestampMs}`,
      type: isReceived ? 'interest' : 'interest-fee',
      timestamp: item.timestampMs,
      transfers: [
        {
          asset,
          amount: absoluteValue(item.quantity),
          direction: isReceived ? 'in' : 'out',
        },
      ],
    })
  }

  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: collected.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
